import * as Sentry from '@sentry/react'
import React, { useEffect, useRef, useState } from 'react'
import type { StitchConnectionData } from '../types'

const STITCH_CSS_HREF = 'https://cdn.fastenhealth.com/connect/v4/fasten-stitch-element.css'
const STITCH_JS_SRC = 'https://cdn.fastenhealth.com/connect/v4/fasten-stitch-element.js'
const AUTO_OPEN_TIMEOUT_MS = 8000

type StitchElement = HTMLElement & { show?: () => void }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Inject the Fasten Stitch CDN assets once (idempotent across mounts). */
function ensureStitchAssetsLoaded(): void {
  if (typeof document === 'undefined') return
  if (!document.querySelector(`link[href="${STITCH_CSS_HREF}"]`)) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = STITCH_CSS_HREF
    document.head.appendChild(link)
  }
  if (!document.querySelector(`script[src="${STITCH_JS_SRC}"]`)) {
    const script = document.createElement('script')
    script.type = 'module'
    script.src = STITCH_JS_SRC
    document.head.appendChild(script)
  }
}

/**
 * Opens the modal once the widget has rendered its own view: `show()` dereferences Angular's
 * ViewChild for the <dialog>, so calling it before that child exists throws.
 *
 * `revealTrigger` un-hides the widget's built-in button. It is for FAILING to open — the
 * timeout and a throwing show() — where our hidden trigger would otherwise strand the card
 * with no way forward.
 *
 * A close AFTER a successful open is not a failure: it is the user dismissing the dialog with
 * Escape or the backdrop, which the widget does not report on its eventBus. That is the same
 * event as its own X button, so it goes to `onDismiss`. Revealing the vendor's trigger there
 * left a foreign-styled button, and a stray "Start over", on a card whose flow was simply over.
 */
function autoOpenWhenReady(el: StitchElement, revealTrigger: () => void, onDismiss: () => void): () => void {
  let settled = false
  let dialog: HTMLDialogElement | null = null
  const stop = () => {
    observer.disconnect()
    clearTimeout(timer)
  }

  const attempt = () => {
    const found = el.querySelector('dialog')
    if (settled || !found) return
    settled = true
    stop()
    try {
      el.show?.()
      dialog = found
      dialog.addEventListener('close', onDismiss, { once: true })
    } catch {
      Sentry.captureMessage('hrc: Fasten Stitch show() threw', { level: 'warning' })
      revealTrigger()
    }
  }

  const observer = new MutationObserver(attempt)
  const timer = setTimeout(() => {
    if (settled) return
    settled = true
    stop()
    Sentry.captureMessage('hrc: Fasten Stitch never rendered its view', { level: 'warning' })
    revealTrigger()
  }, AUTO_OPEN_TIMEOUT_MS)

  observer.observe(el, { childList: true, subtree: true })
  attempt()

  return () => {
    stop()
    dialog?.removeEventListener('close', onDismiss)
  }
}

/**
 * FAM-101: keep the widget in light DOM (dialog/iframe sizing) and load CSS via <link> so styles
 * reach the custom element's light-DOM children. Profile toggle translate-x is protected in
 * globals.css with unlayered class rules that beat Fasten's unlayered * reset.
 *
 * `externalId` is Fasten's `external_id` (attribute `external-id`): the HRC handoff nonce, echoed back
 * to the backend on the `patient.connection_success` webhook.
 *
 * `onComplete` fires for EVERY `widget.complete` with the object entries of `data[]` in order (top-level
 * `api_mode` folded in; non-objects dropped), possibly empty. The caller decides which entry is valid, which is
 * forwarded, and how many DISTINCT other valid connections exist, so nothing is silently lost or over-reported.
 * `onMalformed` fires for events that cannot be interpreted at all (untrusted vendor input; never logged raw).
 * `onConfigError` fires when Fasten reports that it could not resolve the widget configuration.
 */
type FastenStitchProps = {
  publicId: string
  externalId?: string
  /** Open the modal ourselves and hide the widget's own button, so the flow needs a single click. */
  autoOpen?: boolean
  onComplete: (entries: StitchConnectionData[]) => void
  onClose?: () => void
  onMalformed?: () => void
  onConfigError?: () => void
  /**
   * The auto-open gave up (timeout, or show() threw) and the widget's own trigger is now
   * visible. The caller owns the recovery copy, which is meaningless until this fires.
   */
  onAutoOpenFailed?: () => void
}

const FastenStitch: React.FC<FastenStitchProps> = ({
  publicId,
  externalId,
  autoOpen = false,
  onComplete,
  onClose,
  onMalformed,
  onConfigError,
  onAutoOpenFailed,
}) => {
  const elementRef = useRef<HTMLElement | null>(null)
  const [triggerVisible, setTriggerVisible] = useState(false)
  const callbacksRef = useRef({ onComplete, onClose, onMalformed, onConfigError, onAutoOpenFailed })
  callbacksRef.current = { onComplete, onClose, onMalformed, onConfigError, onAutoOpenFailed }

  useEffect(() => {
    ensureStitchAssetsLoaded()
  }, [])

  useEffect(() => {
    const el = elementRef.current as StitchElement | null
    if (!el || !autoOpen) return undefined
    return autoOpenWhenReady(
      el,
      () => {
        setTriggerVisible(true)
        callbacksRef.current.onAutoOpenFailed?.()
      },
      () => callbacksRef.current.onClose?.()
    )
  }, [autoOpen, publicId, externalId])

  useEffect(() => {
    const el = elementRef.current
    if (!el) return

    const malformed = () => {
      // Count-only signal: the raw payload may hold identifiers and is never logged.
      Sentry.captureMessage('hrc: unparseable Fasten Stitch eventBus payload', { level: 'warning' })
      callbacksRef.current.onMalformed?.()
    }

    const handler = (event: Event) => {
      const raw = (event as CustomEvent<{ data?: unknown }>).detail?.data
      if (raw === undefined || raw === null || raw === '') return
      let payload: unknown
      try {
        payload = typeof raw === 'string' ? JSON.parse(raw) : undefined
      } catch {
        payload = undefined
      }
      if (!isRecord(payload)) {
        malformed()
        return
      }
      // patient.connection_failed/pending are intentionally ignored: the backend verifies connection state
      // instead of trusting the widget's report.
      if (payload.event_type === 'widget.complete') {
        const list: unknown[] = Array.isArray(payload.data) ? payload.data : []
        const apiMode = typeof payload.api_mode === 'string' ? payload.api_mode : undefined
        const entries = list.filter(isRecord).map((entry) => ({
          ...(entry as StitchConnectionData),
          api_mode: (entry.api_mode as string | undefined) ?? apiMode,
        }))
        callbacksRef.current.onComplete(entries)
      } else if (payload.event_type === 'widget.close') {
        callbacksRef.current.onClose?.()
      } else if (payload.event_type === 'widget.config_error') {
        // Count-only signal: vendor configuration details are never logged.
        Sentry.captureMessage('hrc: Fasten Stitch configuration error', { level: 'warning' })
        callbacksRef.current.onConfigError?.()
      }
    }

    el.addEventListener('eventBus', handler)
    return () => el.removeEventListener('eventBus', handler)
  }, [publicId, externalId])

  return (
    <fasten-stitch-element
      key={`${publicId}:${externalId ?? ''}`}
      ref={elementRef}
      public-id={publicId}
      {...(externalId ? { 'external-id': externalId } : {})}
      {...(autoOpen && !triggerVisible ? { 'data-autoopen': 'true' } : {})}
    />
  )
}

export default FastenStitch
